import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';

const CANONICAL = 'https://e-asy.es/integracion-sistemas';

const SERVICE_LD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Integración de sistemas empresariales',
  description:
    'Integración de CRM, ERP, facturación, ecommerce y otras herramientas para que los datos circulen una sola vez.',
  url: CANONICAL,
  serviceType: 'Integración de sistemas',
  provider: {
    '@type': 'Organization',
    name: 'E-ASY',
    url: 'https://e-asy.es',
  },
  areaServed: { '@type': 'Country', name: 'España' },
};

const FAQ = [
  {
    question: '¿Se puede integrar con otras herramientas?',
    answer:
      'En la mayoría de casos sí, si la herramienta ofrece API o exportaciones razonables. Te decimos con honestidad qué es viable, qué es frágil y qué merece esperar a un cambio de plataforma.',
  },
  {
    question: '¿Integrar CRM y facturación es lo típico?',
    answer:
      'Sí. Es uno de los casos más frecuentes en pymes: oportunidad en el CRM que debe convertirse en pedido y factura sin reescribir líneas. También ecommerce ↔ almacén ↔ contabilidad.',
  },
  {
    question: '¿Cuánto dura un proyecto de integración?',
    answer:
      'Depende del número de sistemas y de la calidad de sus APIs. Un flujo bien acotado puede estar en semanas; proyectos amplios van por fases. Siempre empezamos por un alcance que puedas usar en producción pronto.',
  },
  {
    question: '¿Necesito un ERP nuevo para integrar?',
    answer:
      'No necesariamente. A veces integrar lo actual ya reduce mucho el caos; otras el ERP es parte de la solución. Lo vemos en una primera conversación técnica y de negocio, sin compromiso rígido.',
  },
];

const IntegracionSistemasPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="Integración de sistemas empresariales | APIs y conectores | E-ASY"
    description="Integración de sistemas: CRM, facturación, ERP y tienda online compartiendo los mismos datos. APIs y conectores para empresas de toda España, desde la sede en Tenerife."
    keywords="integración de sistemas, integrar CRM y facturación, APIs empresa, conectores ERP, integración software gestión"
    canonical={CANONICAL}
    h1="Integración de sistemas: una sola verdad para ventas, stock y administración"
    lede="Cuando cada departamento usa su herramienta y los datos no coinciden, la discusión va de números y no de clientes. Nos dedicamos a que los sistemas compartan datos de forma fiable y entendible para el equipo."
    faqItems={FAQ}
    serviceJsonLd={SERVICE_LD}
    serviceJsonLdId="jsonld-service-integracion"
    cobertura
  >
    <h2>Cuando necesitas integrar CRM y facturación (y mucho más)</h2>
    <p>
      El patrón es siempre parecido: el comercial cierra en un sitio, administración
      factura en otro y almacén mira una tercera lista. La{' '}
      <strong>integración de sistemas</strong> consiste en definir qué objeto
      es el maestro (cliente, pedido, producto) y cómo se actualiza cada paso.
    </p>

    <h2>Integración de sistemas sin humo técnico</h2>
    <p>
      APIs, colas, webhooks, ficheros programados: usamos lo que toque, pero
      explicado en lenguaje claro. El objetivo es que sepas qué pasa cuando
      alguien pulsa “guardar” y quién puede tocar qué.
    </p>

    <h2>De la integración a la gestión</h2>
    <p>
      Una vez los sistemas se entienden, los avisos, validaciones e informes
      de administración viven en{' '}
      <Link to="/vulpin">Vulpin Suite</Link>
      . Si el problema de fondo es que no hay
      núcleo de gestión, hablamos también de{' '}
      <Link to="/erp-para-pymes">ERP para pymes</Link> o de software adaptado
      en ONG en <Link to="/erp-para-ong">ERP para ONG</Link>.
    </p>

    <p>
      <Link to="/servicios/integraciones-empresariales" className="seo-landing-cta">
        Más sobre integraciones empresariales
      </Link>
    </p>
  </SeoLandingLayout>
);

export default IntegracionSistemasPage;
